<script setup lang="ts">
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import { useImageFile } from "@/stores/queries/files.query";
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";

const props = withDefaults(
  defineProps<{
    id: number;
    hidePreview?: boolean;
    fill?: boolean;
    previewMode?: "button" | "overlay";
  }>(),
  {
    hidePreview: false,
    fill: false,
    previewMode: "button",
  },
);

const { t } = useTranslation();
const {
  data: imageUrl,
  isLoading,
  isError,
  errorCode,
} = useImageFile(props.id);

const show = ref(false);

const srcList = computed(() => {
  return imageUrl.value ? [imageUrl.value] : [];
});

const errorMessage = computed(() => {
  if (!errorCode.value) return null;
  return t(`report:image-errors.${errorCode.value}`);
});

const handleClick = (event: MouseEvent) => {
  // Only stop propagation and open preview if preview is enabled and in overlay mode
  if (!props.hidePreview && props.previewMode === "overlay") {
    event.stopPropagation();
    show.value = true;
  }
  // Otherwise, let the click propagate to parent
};
</script>

<template>
  <el-skeleton
    v-if="isLoading"
    :style="
      fill ? 'width: 100%; height: 100%' : 'width: 100%; aspect-ratio: 16/9'
    "
    :loading="true"
    animated
  >
    <template #template>
      <el-skeleton-item variant="image" style="width: 100%; height: 100%" />
    </template>
  </el-skeleton>

  <div
    v-else-if="isError"
    class="d-flex flex-column align-items-center justify-content-center text-center p-2 image-error-container"
    :style="
      fill ? 'width: 100%; height: 100%' : 'width: 100%; aspect-ratio: 16/9'
    "
  >
    <i class="bi bi-exclamation-triangle fs-4 text-warning mb-1"></i>
    <span class="small text-muted">{{ errorMessage }}</span>
  </div>

  <div
    v-else
    class="position-relative hover-parent"
    :class="{ 'overlay-mode': !hidePreview && previewMode === 'overlay' }"
    @click="handleClick"
  >
    <img
      :src="imageUrl"
      alt="Image"
      :style="
        fill ? 'width: 100%; height: 100%' : 'width: 100%; aspect-ratio: 16/9'
      "
      draggable="false"
      style="object-fit: cover"
    />
    <template v-if="!hidePreview">
      <!-- Mode button (default) -->
      <DRBtnIcon
        v-if="previewMode === 'button'"
        @click.stop="show = true"
        class="hover-child absolute-btn"
        icon="bi-eye"
        :size="28"
        variant="outline"
      />
      <!-- Mode overlay -->
      <div v-if="previewMode === 'overlay'" class="preview-overlay">
        <el-icon size="32" class="preview-icon">
          <i class="bi-eye"></i>
        </el-icon>
      </div>
      <el-image-viewer
        hide-on-click-modal
        v-if="show"
        :url-list="srcList"
        show-progress
        @close="show = false"
        teleported
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.image-error-container {
  background-color: var(--el-fill-color-light);
  border: 1px dashed var(--el-border-color);
  border-radius: 4px;
}

.overlay-mode {
  cursor: pointer;

  .preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
  }

  .preview-icon {
    color: white;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    .preview-overlay {
      background-color: rgba(0, 0, 0, 0.5);
    }

    .preview-icon {
      opacity: 1;
    }
  }
}
</style>
